import {
  Answers,
  CONTACT_FIELD_KEYS,
  ContactInput,
  FormSubmitPayload,
  LeadInput,
} from '@jlg-website/shared';
import { ContactFilledOut, LeadCreated, QuestionsFilledOut } from '@/hooks/contact/events';
import { enqueue } from '@/lib/marketing/on_interaction/gtm';
import type useGoogleConversion from '@/hooks/useGoogleConversion';
import type { MarketingValues } from '@/providers/marketing/campaign';
import { compute as computeScore } from './score';

type MarketingConversion = ReturnType<typeof useGoogleConversion>;

// The contact is still empty when the visitor first reaches the contact step.
const EMPTY_CONTACT: ContactInput = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  zip_code: '',
  details: null,
  consent: false,
};

// GA keeps only the first 16 characters of an event dedup id, so the id is cut to that length.
const DEDUP_ID_LENGTH = 16;

function sessionId(): string {
  return window.JLG?.analytics?.session?.session_id ?? '';
}

// Campaign attribution for the Google conversion events, in the shape `useGoogleConversion`
// expects.
function marketingCampaign(marketing: MarketingValues) {
  return {
    id: marketing.campaignId,
    lead_type_id: marketing.leadTypeId,
    offer_id: marketing.offerId,
    lead_type_name: marketing.leadTypeName,
    lead_score_baseline: marketing.leadScoreBaseline,
  };
}

// Coerces the engine's answers to the `LeadInput` shape: each value becomes a non-empty
// array and each score defaults to `[0]`, matching what the lead endpoint stores. Blank
// answers are dropped. Mirrors the server's `collectAnswers`.
export function toLeadAnswers(answers: Answers): LeadInput['lead']['answers'] {
  const coerced: LeadInput['lead']['answers'] = {};
  for (const [questionId, answer] of Object.entries(answers)) {
    const value = Array.isArray(answer.value)
      ? answer.value
      : answer.value === null || answer.value === undefined
        ? []
        : [answer.value];
    if (value.length === 0) continue;
    coerced[questionId] = { value, score: answer.score.length > 0 ? answer.score : [0] };
  }

  return coerced;
}

// The analytics events (`hooks/contact/events`) are typed on the full `LeadInput` but only
// read `lead.answers` and `lead.contact`. We build a complete, valid `LeadInput` here — the
// remaining fields are placeholders the events never read — so this stays confined to the
// form component without reshaping the shared event.
function toLeadEventInput(
  {
    answers,
    contact,
    isQualified,
  }: { answers: Answers; contact: ContactInput; isQualified: boolean },
  marketing: MarketingValues
): LeadInput {
  return {
    session: sessionId(),
    form: { id: 0 },
    campaign: {
      id: marketing.campaignId,
      lead_type: {
        id: marketing.leadTypeId,
        lead_type_name: marketing.leadTypeName,
      },
      lead_source: { id: marketing.leadSourceId },
      lead_score_baseline: marketing.leadScoreBaseline,
      offer: { id: marketing.offerId },
    },
    lead: {
      answers: toLeadAnswers(answers),
      contact,
      is_qualified: isQualified,
      is_duplicate: false,
      score: computeScore(answers, marketing.leadScoreBaseline),
    },
    is_2fa_enabled: false,
    analytics: { ip_address: null, user_agent: null, url: null },
  };
}

function pushGaEvent(event: string, attrs: Record<string, unknown> = {}): void {
  const fnc = () => {
    enqueue({
      event,
      session_id: window.JLG?.analytics?.session?.session_id ?? '',
      visitor_id: window.JLG?.analytics?.session?.visitor_id ?? '',
      ...attrs,
    });
  };

  // Only push the event when the there is a session generated by the analytics
  if (window.JLG?.analytics?.session) {
    fnc();
    return;
  }

  // The event is emitted by Analytics script onload.
  // The script is in the project https://github.com/Advocate-Alliance-Group/analytics
  window.addEventListener('JLG:Analytics:loaded', function () {
    fnc();
  });
}

/**
 * Emits a GA progress event once per step as the visitor advances (never on submit),
 * mirroring the `form_progress_*` events the contact form pushes in useContact. `emitted`
 * holds the steps already reported so each fires at most once.
 *
 * `formSessionId` identifies the mounted form the event came from — see `use-lead-form`, which
 * owns it. It is what keeps the `formStep` dedup id unique across the forms sharing a page (the
 * mass-tort hero and the footer both report step 1 on mount), while still collapsing a repeat of
 * the same step from the same form. A random id per push, which is what this used to send, meant
 * GA could never dedup anything.
 *
 * The id carries no separators and is exactly `DEDUP_ID_LENGTH` characters — GA truncates
 * anything longer. Keeping the tail is what preserves the lead type and the step at the end of
 * the string; only the leading random characters of the session id are dropped, and 16 of them
 * are more than enough to keep two forms on the same page apart.
 */
export function emitStepProgress({
  emitted,
  formSessionId,
  payload,
  marketing,
}: {
  emitted: Set<number>;
  formSessionId: string;
  payload: FormSubmitPayload;
  marketing: MarketingValues;
}): void {
  if (emitted.has(payload.current_step)) return;
  emitted.add(payload.current_step);

  const dedupId = `${formSessionId}${marketing.leadTypeId}${payload.current_step}`.slice(
    -DEDUP_ID_LENGTH
  );

  pushGaEvent(`form_progress_${payload.current_step}`);
  pushGaEvent('formStep', {
    event: 'formStep',
    oid: marketing.offerId,
    cid: marketing.campaignId,
    ltid: marketing.leadTypeId,
    step: payload.current_step,
    lead_value: computeScore(payload.answers, marketing.leadScoreBaseline),
    form_response: payload.answers,
    eventDedupId: dedupId,
    version: '2',
  });
}

/** Emits the GA `form_submit` event as the visitor submits, mirroring useContact. */
export function emitFormSubmit(): void {
  pushGaEvent('form_submit');
}

/**
 * Flattens the submit payload into the question answers keyed by question id, dropping the
 * fixed contact fields but keeping questions interleaved into the contact block. Mirrors the
 * server's `collectAnswers` split, letting a single-step form report its questions before
 * the lead is submitted.
 */
export function collectQuestionAnswers(payload: FormSubmitPayload): Answers {
  const answers: Answers = {};
  const contactFieldKeys = CONTACT_FIELD_KEYS as readonly string[];

  for (const [key, answer] of Object.entries(payload.answers)) {
    if (contactFieldKeys.includes(key)) continue;
    answers[key] = answer;
  }

  return answers;
}

/**
 * Fired every time the visitor reaches the last (contact) step of a multi-step form: the
 * questions are done, so we push the "questions filled out" analytics event and the
 * CompleteRegistration conversion with all the answers gathered so far.
 */
export function emitQuestionsFilledOut({
  conversion,
  payload,
  marketing,
}: {
  conversion: MarketingConversion;
  payload: FormSubmitPayload;
  marketing: MarketingValues;
}): void {
  const input = toLeadEventInput(
    { answers: payload.answers, contact: EMPTY_CONTACT, isQualified: payload.is_qualified },
    marketing
  );

  QuestionsFilledOut(input, marketing);
  conversion.sendCompleteRegistrationEvent(sessionId(), marketingCampaign(marketing), payload);
}

/**
 * Fired after the lead is persisted, in order: the contact is captured (ContactFilledOut),
 * the lead row is acknowledged (LeadCreated) and the Lead conversion is sent. Uses the
 * answers and contact the server actually stored.
 */
export function emitLeadSubmitted({
  conversion,
  leadId,
  answers,
  contact,
  isQualified,
  marketing,
}: {
  conversion: MarketingConversion;
  leadId: number;
  answers: Answers;
  contact: ContactInput;
  isQualified: boolean;
  marketing: MarketingValues;
}): void {
  const eventInput = toLeadEventInput({ answers, contact, isQualified }, marketing);

  ContactFilledOut(eventInput, marketing);
  LeadCreated(leadId, eventInput, marketing);
  conversion.sendLeadEvent(sessionId(), marketingCampaign(marketing), answers, contact);
}
